<template>
  <div id="trainStation">
    <div id="topMessage">Drag and drop the train and the observer horizontally. Jump the observer between train and platform (mouse wheel click on the observer). Watch the clocks!</div>
    <div id="scene">
      <v-stage :config="configCanvas" class="simulation-canvas">
        <v-layer>
          <v-group name="platformGroup" ref="container" :config="configPlatformGroup">
            <v-rect name="platform" :config="configPlatform"></v-rect>
            <v-rect name="trainTracks" :config="configTrainTracks"></v-rect>
            <v-rect v-if="isOnLightP1" name="lightP1" :config="configLightP1"></v-rect>
            <v-rect v-if="isOnLightP2" name="lightP2" :config="configLightP2"></v-rect>
            <v-rect v-if="isOnLightP3" name="lightP3" :config="configLightP3"></v-rect>
            <v-rect v-if="isOnLightP4" name="lightP4" :config="configLightP4"></v-rect>
            <v-rect v-if="isOnLightP5" name="lightP5" :config="configLightP5"></v-rect>
            <v-group name="observerGroup" v-if="!isObserverInTrain" :config="configObserverGroup"  @mousedown="handleObserverMouseDown" @mouseup="handleObserverMouseUp" @dragmove="handleObserverDragMove">
              <v-line v-if="isObserverPassingTrain" name="observerTopLine" :config="configObserverTopLine"></v-line>
              <v-rect v-if="isObserverPassingTrain" name="observerTopIndicator" :config="configObserverTopIndicator"></v-rect>
              <v-text v-if="isObserverPassingTrain" name="observerTopText" :config="configObserverTopText"></v-text>
              <v-rect name="observer" :config="configObserver"></v-rect>
              <v-line name="observerBottomLine" :config="configObserverBottomLine"></v-line>
              <v-rect name="observerBottomIndicator" :config="configObserverBottomIndicator"></v-rect>
              <v-text name="observerBottomText" :config="configObserverBottomText"></v-text>
            </v-group>
            <v-rect name="platformLeftIndicator" :config="configPlatformLeftIndicator"></v-rect>
            <v-text name="platformLeftText" :config="configPlatformLeftText"></v-text>
            <v-rect name="platformMiddleIndicator" :config="configPlatformMiddleIndicator"></v-rect>
            <v-text name="platformMiddleText" :config="configPlatformMiddleText"></v-text>
            <v-rect name="platformRightIndicator" :config="configPlatformRightIndicator"></v-rect>
            <v-text name="platformRightText" :config="configPlatformRightText"></v-text>
          </v-group>
          <v-group name="trainGroup" ref="container" :config="configTrainGroup" @dragmove="handleTrainGroupDragMove">
            <v-rect name="train" :config="configTrain"></v-rect>
            <v-rect v-if="isOnLightT1" name="lightT1" :config="configLightT1"></v-rect>
            <v-rect v-if="isOnLightT2" name="lightT2" :config="configLightT2"></v-rect>
            <v-rect v-if="isOnLightT3" name="lightT3" :config="configLightT3"></v-rect>
            <v-rect v-if="isOnLightT4" name="lightT4" :config="configLightT4"></v-rect>
            <v-rect v-if="isOnLightT5" name="lightT5" :config="configLightT5"></v-rect>
            <v-group name="observerGroup" v-if="isObserverInTrain" :config="configObserverGroup" @mousedown="handleObserverMouseDown" @mouseup="handleObserverMouseUp" @dragmove="handleObserverDragMove">
              <v-line name="inTrainObserverTopLine" :config="configInTrainObserverTopLine"></v-line>
              <v-rect name="observerTopIndicator" :config="configObserverTopIndicator"></v-rect>
              <v-text name="observerTopText" :config="configObserverTopText"></v-text>
              <v-rect name="observer" :config="configObserver"></v-rect>
              <v-line v-if="isObserverPassingTrain" name="inTrainObserverBottomLine" :config="configInTrainObserverBottomLine"></v-line>
              <v-rect v-if="isObserverPassingTrain" name="observerBottomIndicator" :config="configObserverBottomIndicator"></v-rect>
              <v-text v-if="isObserverPassingTrain" name="observerBottomText" :config="configObserverBottomText"></v-text>
            </v-group>
            <v-rect name="trainLeftIndicator" :config="configTrainLeftIndicator"></v-rect>
            <v-text name="trainLeftText" :config="configTrainLeftText"></v-text>
            <v-rect name="trainMiddleIndicator" :config="configTrainMiddleIndicator"></v-rect>
            <v-text name="trainMiddleText" :config="configTrainMiddleText"></v-text>
            <v-rect name="trainRightIndicator" :config="configTrainRightIndicator"></v-rect>
            <v-text name="trainRightText" :config="configTrainRightText"></v-text>
          </v-group>
        </v-layer>
      </v-stage>
    </div>
    <div id="info" class="info-row">
      <div id="assumptions"><span class="assumptionHighlight" v-if="lightsEnabled">- All lights turn on at midnight</span>
          <br>- Train speed: <span class="assumptionHighlight">{{speedValue}} [{{speedUnit}}]</span><br>
          - Train length: 100 m <span class="assumptionHighlight" v-if="!isObserverInTrain && physics=='relativistic'">(shortened to {{shortenedObject}} m)</span><br>
          - Platform length: 100 m <span class="assumptionHighlight" v-if="isObserverInTrain && physics=='relativistic'">(shortened to {{shortenedObject}} m)</span><br>
          - <span class="assumptionHighlight">Both middle clocks display midnight when they're meeting</span>
      </div>
      <div id="help">
        &nbsp;<br>
        <span class="assumptionHighlight">IMPORTANT:</span><br>
        You can jump with the observer only <br>if the animation is paused and the object you<br>want to jump in is currently meeting the observer.<br>
      </div>
    </div>
    <div id="controls">
      <button id="lightsSwitch" v-bind:class="{ 'active': lightsEnabled}" v-on:click="handleLightsSwitchClick">Lights</button>
      <button id="observerJump" :disabled="!isJumpButtonActive" v-on:click="observerJump">Jump observer</button>
      <button id="reset" v-on:click="handleResetButtonClick">Reset</button>
      <button id="playPause" v-bind:class="{ 'active': isSimulationPlaying}" v-on:click="handlePlayPauseButtonClick">{{ playPauseButtonText }}</button>
    </div>
  </div>
</template>

<script>
import Scene from '@/domain/scene/Scene.js'
import sceneStore from '@/vuex/sceneStore'

const FULL_WIDTH_MARGIN = 20
const MIN_SCENE_WIDTH = 816

export default {
  name: 'TrainStation',
  props: {
    sceneId: String,
    title: String,
    physics: String,
    speedValue: Number,
    speedUnit: String,
    sceneWidth: Number
  },
  store: sceneStore,
  data () {
    let scene = new Scene(
      this.sceneId,
      this.title,
      this.physics,
      this.speedValue,
      this.speedUnit
    )
    scene.setElements(this.getSceneWidth())
    scene.preloadImages(this.getSceneWidth())
    return {
      scene: scene
    }
  },
  computed: {
    configCanvas: function () {
      return this.scene.getCanvasConfig()
    },
    configPlatform: function () {
      return this.scene.platform.getConfig()
    },
    configTrainTracks: function () {
      return this.scene.trainTracks.getConfig()
    },
    configPlatformGroup: function () {
      return this.scene.platformGroup.getConfig()
    },
    configTrain: function () {
      return this.scene.train.getConfig()
    },
    configTrainGroup: function () {
      return this.scene.trainGroup.getConfig()
    },
    configObserver: function () {
      return this.scene.observer.getConfig()
    },
    configObserverGroup: function () {
      return this.scene.observerGroup.getConfig()
    },
    configObserverTopIndicator: function () {
      return this.scene.indicators.observer_top_indicator.getConfig()
    },
    configObserverTopText: function () {
      return this.scene.indicators.observer_top_indicator.getTextConfig()
    },
    configObserverTopLine: function () {
      return this.scene.indicators.observer_top_line.getConfig()
    },
    configInTrainObserverTopLine: function () {
      return this.scene.indicators.in_train_observer_top_line.getConfig()
    },
    configObserverBottomIndicator: function () {
      return this.scene.indicators.observer_bottom_indicator.getConfig()
    },
    configObserverBottomText: function () {
      return this.scene.indicators.observer_bottom_indicator.getTextConfig()
    },
    configObserverBottomLine: function () {
      return this.scene.indicators.observer_bottom_line.getConfig()
    },
    configInTrainObserverBottomLine: function () {
      return this.scene.indicators.in_train_observer_bottom_line.getConfig()
    },
    configPlatformLeftIndicator: function () {
      return this.scene.indicators.platform_left_indicator.getConfig()
    },
    configPlatformLeftText: function () {
      return this.scene.indicators.platform_left_indicator.getTextConfig()
    },
    configPlatformMiddleIndicator: function () {
      return this.scene.indicators.platform_middle_indicator.getConfig()
    },
    configPlatformMiddleText: function () {
      return this.scene.indicators.platform_middle_indicator.getTextConfig()
    },
    configPlatformRightIndicator: function () {
      return this.scene.indicators.platform_right_indicator.getConfig()
    },
    configPlatformRightText: function () {
      return this.scene.indicators.platform_right_indicator.getTextConfig()
    },
    configTrainLeftIndicator: function () {
      return this.scene.indicators.train_left_indicator.getConfig()
    },
    configTrainLeftText: function () {
      return this.scene.indicators.train_left_indicator.getTextConfig()
    },
    configTrainMiddleIndicator: function () {
      return this.scene.indicators.train_middle_indicator.getConfig()
    },
    configTrainMiddleText: function () {
      return this.scene.indicators.train_middle_indicator.getTextConfig()
    },
    configTrainRightIndicator: function () {
      return this.scene.indicators.train_right_indicator.getConfig()
    },
    configTrainRightText: function () {
      return this.scene.indicators.train_right_indicator.getTextConfig()
    },
    configLightP1: function () {
      return this.scene.lights.light_p1.getConfig()
    },
    configLightP2: function () {
      return this.scene.lights.light_p2.getConfig()
    },
    configLightP3: function () {
      return this.scene.lights.light_p3.getConfig()
    },
    configLightP4: function () {
      return this.scene.lights.light_p4.getConfig()
    },
    configLightP5: function () {
      return this.scene.lights.light_p5.getConfig()
    },
    configLightT1: function () {
      return this.scene.lights.light_t1.getConfig()
    },
    configLightT2: function () {
      return this.scene.lights.light_t2.getConfig()
    },
    configLightT3: function () {
      return this.scene.lights.light_t3.getConfig()
    },
    configLightT4: function () {
      return this.scene.lights.light_t4.getConfig()
    },
    configLightT5: function () {
      return this.scene.lights.light_t5.getConfig()
    },
    isOnLightP1: function () {
      return this.scene.lights.light_p1.isLightOn
    },
    isOnLightP2: function () {
      return this.scene.lights.light_p2.isLightOn
    },
    isOnLightP3: function () {
      return this.scene.lights.light_p3.isLightOn
    },
    isOnLightP4: function () {
      return this.scene.lights.light_p4.isLightOn
    },
    isOnLightP5: function () {
      return this.scene.lights.light_p5.isLightOn
    },
    isOnLightT1: function () {
      return this.scene.lights.light_t1.isLightOn
    },
    isOnLightT2: function () {
      return this.scene.lights.light_t2.isLightOn
    },
    isOnLightT3: function () {
      return this.scene.lights.light_t3.isLightOn
    },
    isOnLightT4: function () {
      return this.scene.lights.light_t4.isLightOn
    },
    isOnLightT5: function () {
      return this.scene.lights.light_t5.isLightOn
    },
    lightsEnabled: function () {
      return this.scene.lightsEnabled
    },
    isObserverInTrain: function () {
      return this.scene.observerGroup.isInTrain
    },
    isObserverPassingTrain () {
      return this.scene.isObserverPassingTrain()
    },
    isSimulationPlaying () {
      return this.scene.isSimulationPlaying
    },
    playPauseButtonText () {
      return this.scene.isSimulationPlaying ? 'Pause' : 'Play'
    },
    isJumpButtonActive () {
      return (this.scene.canObserverJump() && !this.scene.isSimulationPlaying)
    },
    shortenedObject () {
      let objectNormalLength = 100
      return this.scene.lengthContraction(objectNormalLength).toFixed(2)
    }
  },
  created () {
    window.addEventListener('resize', this.handleResize)
  },
  destroyed () {
    window.removeEventListener('resize', this.handleResize)
    this.scene.isSimulationPlaying = false
  },
  methods: {
    handleTrainGroupDragMove: function (e) {
      if (e.target.attrs.name === 'trainGroup') {
        this.scene.isSimulationPlaying = false
        this.scene.trainGroup.x = e.target.x()
        this.scene.trainGroupCurrentX = e.target.x()
        this.scene.updateScene()
      }
    },
    handleObserverDragMove: function (e) {
      if (e.target.attrs.name === 'observerGroup') {
        this.scene.isSimulationPlaying = false
        this.scene.observerGroup.x = e.target.x()
        this.scene.observerGroupCurrentX = e.target.x()
        this.scene.updateScene()
      }
    },
    handleObserverMouseDown: function (e) {
      if (e.evt.which === 2 || e.evt.which === 4) {
        this.observerWheelClickStart(e)
      }
    },
    handleObserverMouseUp: function (e) {
      if (e.evt.which === 2 || e.evt.which === 4) {
        this.observerWheelClickStop(e)
      }
    },
    observerWheelClickStart: function (e) {
      this.wheelClickTarget = e.target
    },
    observerWheelClickStop: function (e) {
      if (this.wheelClickTarget === e.target) {
        this.observerJump(e)
      }
      this.wheelClickTarget = null
    },
    observerJump: function (e) {
      this.scene.jumpObserver()
    },
    handleResize: function (e) {
      this.scene.setElements(this.getSceneWidth())
      if (this.scene.isSimulationPlaying) {
        this.scene.isSimulationPlaying = false
        let that = this
        setTimeout(function () {
          that.scene.resetSimulation()
        }, 30)
      }
    },
    getSceneWidth: function () {
      let newSceneWidth = document.getElementById('app').offsetWidth - FULL_WIDTH_MARGIN * 2
      if (newSceneWidth < MIN_SCENE_WIDTH) {
        newSceneWidth = MIN_SCENE_WIDTH
      }
      return newSceneWidth
    },
    handleResetButtonClick: function (e) {
      this.scene.resetSimulation()
    },
    handlePlayPauseButtonClick: function (e) {
      this.scene.playPause()
    },
    handleLightsSwitchClick: function (e) {
      this.scene.switchLights(this.getSceneWidth())
    }
  }
}

</script>

<style lang="scss">
$bg-color: #333;

#trainStation {
  min-width: 840px;
}

#scene {
  background-color: $bg-color;
  margin: 0 12px;
}

.simulation-canvas .konvajs-content {
  margin: 0 auto;
  background-color: $bg-color;
}

#controls {
  background-color: #444;
  margin: 0 12px;
  color: #bbb;
  border-top: 3px solid #4d94d1;
}

#controls button.active {
  background-color: #4d94d1;
  color: #fff;
}

#controls button.active:hover {
  background-color: #71a9da;
}

#controls button#lightsSwitch {
  margin-right: 7% !important;
  width: 180px;
}

#controls button {
  background-color: #bbb;
  border: none;
  color: #333;
  font-weight: bold;
  padding: 10px 15px;
  text-align: center;
  text-decoration: none;
  display: inline-block;
  font-size: 16px;
  margin: 10px;
  min-width: 80px;
}

#controls button:hover {
  cursor: pointer;
  background-color: #ddd;
}

#controls button#observerJump {
  margin-right: 7%;
  width: 180px;
}

#controls button#observerJump:disabled {
  color: #555;
  background-color: #999;
  cursor: not-allowed;
}

#controls button#reset {
  background-color: #ff436f;
  color: #fff;
}

#controls button#reset:hover {
  background-color: #ff668a;
}

#info {
  background-color: $bg-color;
  margin: 0 12px;
  color: #bbb;
  font-size: 14px;
  line-height: 1.5;
}

#info div {
  padding: 0 10px 10px 10px;
}

#assumptions {
  float: left;
  width: 45%;
  text-align: left;
}

#help {
  float: right;
  width: 45%;
  text-align: right;
}

.info-row:after {
  content: "";
  display: table;
  clear: both;
}

.assumptionHighlight {
  color: #71a9da;
}

#topMessage {
  background-color: #4d94d1;
  color: #fff;
  margin: 0 12px;
  padding: 6px;
  font-size: calc(6px + 0.5vw);
}

</style>
